import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MsalProvider, MsalAuthenticationTemplate } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { msalInstance, authDisabled, loginRequest, clearStuckInteraction, onAuthFailed, forceReLogin } from '@/auth/msal';
import { ToastProvider } from '@/components/Toast';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import App from './App';
import './styles.css';
import { disableAutofill } from '@/lib/noAutofill';

// Item 28: keep password managers from filling the app's form fields.
disableAutofill();

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

const forceSignIn = forceReLogin;

/**
 * Full-screen recovery overlay. Guarantees an auth problem is NEVER a silent blank:
 * if sign-in can't recover automatically, the user always gets a visible Sign-in button.
 */
function AuthRecovery() {
  const [failed, setFailed] = useState(false);
  useEffect(() => onAuthFailed(() => setFailed(true)), []);
  if (!failed) return null;
  return (
    <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-4 bg-background/95 p-6 text-center">
      <div className="text-lg font-semibold text-primary">Your session needs to be refreshed</div>
      <div className="max-w-md text-sm text-muted-foreground">
        We couldn&apos;t sign you in automatically. Click below to sign in again.
      </div>
      <button className="btn-primary" onClick={forceReLogin}>Sign in</button>
    </div>
  );
}

/** Sign-in status with an escape hatch — if it stalls (e.g. a stuck interaction), a
 * button lets the user recover instead of staring at a blank/loading screen. */
function Loading({ msg }: { msg: string }) {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 6000);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
      <div>{msg}</div>
      {slow && (
        <button className="btn-primary" onClick={forceSignIn}>Sign in</button>
      )}
    </div>
  );
}

function AppTree() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter>
          <ErrorBoundary>
            <App />
          </ErrorBoundary>
          <AuthRecovery />
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  );
}

function Root() {
  if (authDisabled) return <AppTree />;
  return (
    <MsalProvider instance={msalInstance}>
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={loginRequest}
        loadingComponent={() => <Loading msg="Signing in…" />}
        errorComponent={({ error }) => {
          clearStuckInteraction();
          return (
            <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-sm text-muted-foreground">
              <div>Sign-in error: {error?.message ?? 'unknown'}</div>
              <button className="btn-primary" onClick={forceSignIn}>Sign in again</button>
            </div>
          );
        }}
      >
        <AppTree />
      </MsalAuthenticationTemplate>
    </MsalProvider>
  );
}

// MSAL v3 must be initialized (and any redirect response handled) before render.
async function bootstrap() {
  const root = ReactDOM.createRoot(document.getElementById('root')!);
  if (!authDisabled) {
    try {
      await msalInstance.initialize();
      await msalInstance.handleRedirectPromise();
    } catch {
      /* an interrupted/failed redirect must not blank the app */
    }
    // Wipe any leftover "interaction in progress" flag — otherwise an interrupted
    // login stays stuck in localStorage and the app renders blank on every reload.
    clearStuckInteraction();
    // Set the active account if we have one (e.g. after redirect)
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length && !msalInstance.getActiveAccount()) {
      msalInstance.setActiveAccount(accounts[0]);
    }
  }
  root.render(
    <React.StrictMode>
      <Root />
    </React.StrictMode>,
  );
}

void bootstrap();
